import React from "react";
import { Eye, BookOpen, Layers, Paperclip, CheckCircle2, Send, RefreshCw, FileEdit, CalendarClock } from "lucide-react";
import Toggle from "modules/swiftboard/components/ui/Toggle";
import DateTimePicker from "modules/swiftboard/components/ui/DateTimePicker";
import LocalDateTime from "services/date-converter";
import { BlockPreview } from "./BlockCard";

const STATUS_OPTIONS = [
  { value: "DRAFT", label: "Save as Draft", desc: "Keep working — not visible to anyone yet.", icon: FileEdit },
  { value: "PUBLISHED", label: "Publish Now", desc: "Goes live immediately after you submit.", icon: Send },
  { value: "SCHEDULED", label: "Schedule", desc: "Pick a future date & time to auto-publish.", icon: CalendarClock },
];

const SummaryRow = ({ icon: Icon, label, value }) => (
  <div className="flex items-center gap-3 py-2.5 border-b border-border last:border-0">
    <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-surface_alt flex items-center justify-center">
      <Icon size={13} className="text-text_muted" />
    </div>
    <div className="min-w-0 flex-1">
      <p className="text-[10px] uppercase tracking-wide text-text_muted font-semibold">{label}</p>
      <p className="text-[13px] text-text_primary font-medium truncate">{value}</p>
    </div>
  </div>
);

const ReviewSection = ({
  title,
  blocks,
  meta,
  bannerImage,
  knowledgeBaseLabel,
  isActivated,
  onToggleActivated,
  status,
  onStatusChange,
  scheduledAt,
  onScheduledAtChange,
  publishedAt,
  onSubmit,
  isSubmitting,
  isEditMode,
}) => {
  const isScheduleInPast = status === "SCHEDULED" && scheduledAt && scheduledAt.getTime() <= Date.now();

  return (
  <div className="max-w-5xl mx-auto space-y-5">
    <div className="flex items-center gap-3">
      <div className="w-10 h-10 rounded-xl bg-primary_tint flex items-center justify-center flex-shrink-0">
        <Eye size={18} className="text-primary" />
      </div>
      <div>
        <h3 className="text-[15px] font-semibold text-text_primary">Review &amp; Submit</h3>
        <p className="text-[12.5px] text-text_muted mt-0.5">
          This is exactly how the article will look. Confirm the status and submit.
        </p>
      </div>
    </div>

    <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_300px] gap-5 items-start">
      {/* Live preview */}
      <div className="min-w-0 bg-surface border border-border rounded-2xl overflow-hidden shadow-sm">
        <div className="flex items-center gap-2 px-5 py-3 border-b border-border bg-surface_alt/50">
          <span className="w-1.5 h-1.5 rounded-full bg-success" />
          <span className="text-[11px] font-semibold uppercase tracking-wide text-text_muted">Live Preview</span>
        </div>
        {bannerImage && (
          <img src={bannerImage} alt="Article banner" className="w-full h-48 object-cover" />
        )}
        <div className="px-8 py-8 min-w-0 break-normal [overflow-wrap:break-word] [word-break:normal] [hyphens:none]">
          <h1 className="text-[26px] font-bold text-text_primary leading-tight mb-6 tracking-tight">
            {title || "Untitled article"}
          </h1>
          <div className="space-y-5">
            {blocks.length === 0 ? (
              <p className="text-sm text-text_muted italic">No content blocks added yet.</p>
            ) : (
              blocks.map((block) => <BlockPreview key={block.id} block={block} />)
            )}
          </div>
        </div>
      </div>

      {/* Summary + publish settings + submit */}
      <div className="space-y-4">
        <div className="bg-surface border border-border rounded-2xl p-4 shadow-sm">
          <p className="text-[13px] font-semibold text-text_primary mb-0.5">Summary</p>
          <p className="text-[11px] text-text_muted mb-2">Double-check the details below.</p>
          <SummaryRow icon={BookOpen} label="Knowledge Base" value={knowledgeBaseLabel || "Not selected"} />
          <SummaryRow icon={Layers} label="Content Blocks" value={`${blocks.length} block${blocks.length === 1 ? "" : "s"}`} />
          <SummaryRow
            icon={Paperclip}
            label="Attachments"
            value={`${meta.attachments.length} file${meta.attachments.length === 1 ? "" : "s"}`}
          />
        </div>

        <div className="bg-surface border border-border rounded-2xl p-4 shadow-sm space-y-3">
          <div>
            <p className="text-[13px] font-semibold text-text_primary mb-0.5">Publish Settings</p>
            <p className="text-[11px] text-text_muted">Choose when this article goes live.</p>
          </div>

          <div className="space-y-2">
            {STATUS_OPTIONS.map((opt) => {
              const Icon = opt.icon;
              const selected = status === opt.value;
              return (
                <button
                  type="button"
                  key={opt.value}
                  onClick={() => onStatusChange(opt.value)}
                  className={`w-full flex items-start gap-2.5 rounded-lg border px-3 py-2.5 text-left ${
                    selected ? "border-primary bg-primary_tint" : "border-border bg-surface"
                  }`}
                >
                  <Icon size={15} className={`flex-shrink-0 mt-0.5 ${selected ? "text-primary" : "text-text_muted"}`} />
                  <div className="min-w-0">
                    <p className={`text-[12.5px] font-semibold ${selected ? "text-primary" : "text-text_primary"}`}>
                      {opt.label}
                    </p>
                    <p className="text-[10.5px] text-text_muted mt-0.5 leading-snug">{opt.desc}</p>
                  </div>
                </button>
              );
            })}
          </div>

          {status === "SCHEDULED" && (
            <div>
              <label className="block text-[11px] font-semibold uppercase tracking-wide text-text_muted mb-1.5">
                Publish Date &amp; Time
              </label>
              <DateTimePicker
                value={scheduledAt}
                onChange={onScheduledAtChange}
                minDate={new Date()}
                placeholder="Select when this article should publish"
                hasError={isScheduleInPast}
              />
              {isScheduleInPast && (
                <p className="text-[11px] text-danger mt-1">Publish time must be in the future.</p>
              )}
            </div>
          )}

          {status === "PUBLISHED" && publishedAt && (
            <p className="text-[11px] text-text_muted">
              First published <LocalDateTime utcTime={publishedAt} type="datetime" />
            </p>
          )}
        </div>

        <div className="bg-surface border border-border rounded-2xl p-4 shadow-sm">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${isActivated ? "bg-success_bg" : "bg-danger_bg"}`}>
                <CheckCircle2 size={14} className={isActivated ? "text-success" : "text-danger"} />
              </div>
              <div>
                <p className="text-[13px] font-semibold text-text_primary">{isActivated ? "Active" : "Inactive"}</p>
                <p className="text-[10.5px] text-text_muted">Visible to users when active</p>
              </div>
            </div>
            <Toggle checked={isActivated} onChange={onToggleActivated} />
          </div>
        </div>

        <button
          type="button"
          onClick={onSubmit}
          disabled={isSubmitting || isScheduleInPast}
          className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold text-white bg-primary shadow-sm disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {isSubmitting ? <RefreshCw size={14} className="animate-spin" /> : <Send size={14} />}
          {isSubmitting ? (isEditMode ? "Updating..." : "Creating...") : isEditMode ? "Update Article" : "Create Article"}
        </button>
      </div>
    </div>
  </div>
  );
};

export default ReviewSection;
